import { shareScope } from '../constants/enum';
import { isOperatorStaff } from './permission';
import type { ShareScope } from '../types/pattern';

/** アクセス判定に必要なユーザー情報（#2804） */
export interface AccessUser {
  uid: string;
  role: number | null | undefined;
  group_ids?: string[];
}

/** 共有範囲を持つリソース（パターン・OCR結果） */
export interface ShareableResource {
  share_scope?: ShareScope;
  shared_group_ids?: string[];
  shared_user_ids?: string[]; // 限定共有時の対象担当者UID（#2963）
  owner_user_id?: string;
}

/**
 * 共有範囲に基づく閲覧可否判定（#2804）
 * - 運営スタッフは全件閲覧可
 * - 企業管理者も group は所属グループのみ閲覧可（一般メンバーと同じ判定。#2886）
 * - company（既定/欠損）: 企業内全員可
 * - private: 作成者のみ
 * - group（限定共有・#2963）: ユーザーの所属グループと共有先グループの積集合が非空、
 *   または uid が共有先担当者（shared_user_ids）に含まれるなら可（OR条件）
 */
export const canViewSharedResource = (user: AccessUser, resource: ShareableResource): boolean => {
  if (isOperatorStaff(user.role)) {
    return true;
  }
  const scope = resource.share_scope ?? shareScope.company;
  switch (scope) {
    case shareScope.company:
      return true;
    case shareScope.private:
      // private は作成者（owner_user_id）のみ。
      // 注意: OCR結果は「継承元パターンの所有者」を owner_user_id として継承するため、
      // パターン作成者と実際のOCR実行者が異なる場合、実行者は自分の結果を閲覧できない。
      // 現状 private は UI から設定不可のため実害は無いが、UIで private を解禁する際は要再設計。
      return resource.owner_user_id === user.uid;
    case shareScope.group: {
      // 限定共有（グループ＋担当者）: いずれかの条件を満たせば可（#2963）
      const userGroups = user.group_ids ?? [];
      const sharedGroups = resource.shared_group_ids ?? [];
      const sharedUsers = resource.shared_user_ids ?? [];
      return (
        sharedGroups.some(groupId => userGroups.includes(groupId)) || sharedUsers.includes(user.uid)
      );
    }
    default:
      return true;
  }
};

/** パターンの閲覧可否（#2804 Phase2） */
export const canViewPattern = canViewSharedResource;
